'use client';
import React, { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import AppImage from './ui/AppImage';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    setMounted(true);
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (href: string) => pathname === href;

  return (
    <header
      className="z-50 transition-all duration-500"
      style={{
        backgroundColor: '#EFE3E1',
        borderBottom: scrolled ? '1px solid rgba(26,26,26,0.08)' : '1px solid transparent',
        boxShadow: scrolled ? '0 1px 20px rgba(26,26,26,0.04)' : 'none'
      }}
      suppressHydrationWarning>

      <style>{`
        .header-nav-link {
          transition: color 0.2s ease, border-color 0.2s ease;
        }
        .header-nav-link:not(.active):hover {
          color: #1A2332!important;
          border-bottom-color: rgba(26,35,50,0.3)!important;
        }
        .header-cta-btn {
          background-color: #1A2332;
          transition: background-color 0.25s ease;
        }
        .header-cta-btn:hover {
          background-color: #2C2C2C;
        }
      `}</style>

      <div
        className="flex items-center justify-between pt-[43px] pb-[35px]"
        style={{ padding: '0 clamp(24px, 4vw, 64px)', height: 'clamp(72px, 7vw, 96px)' }}
        suppressHydrationWarning>

        {/* Logo */}
        <div className="flex-shrink-0">
          <a href="/" style={{ textDecoration: 'none', display: 'block' }}>
            <AppImage
              src="/assets/images/image_2026-06-21_220355733-Photoroom-1782076062922.png"
              alt="Dr Jennifer Morgan — Chartered Clinical Psychologist"
              width={260}
              height={130}
              className="object-contain"
              style={{ background: 'transparent', display: 'block' }}
              priority={true} />
          </a>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center" style={{ gap: 'clamp(24px, 2.5vw, 40px)' }}>
          {[
          { label: 'Home', href: '/' },
          { label: 'About', href: '/about' },
          { label: 'Psychological Services', href: '/psychological-services' },
          { label: 'Concierge Care', href: '/services' },
          { label: 'Appointments & Fees', href: '/enquiries' }].
          map(({ label, href }) =>
          <a
            key={href}
            href={href}
            className={`header-nav-link${isActive(href) ? ' active' : ''}`}
            style={{
              fontFamily: 'adobe-garamond-pro, Georgia, serif',
              fontSize: '15px',
              letterSpacing: '0.04em',
              color: isActive(href) ? '#1A2332' : '#4A4A4A',
              textDecoration: 'none',
              borderBottom: isActive(href) ? '1px solid #1A2332' : '1px solid transparent',
              paddingBottom: '2px'
            }}>
              {label}
            </a>
          )}
        </nav>

        {/* CTA */}
        <div className="hidden lg:block">
          <a
            href="/contact"
            className="header-cta-btn"
            style={{
              fontFamily: 'adobe-garamond-pro, Georgia, serif',
              fontSize: '13px',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: '#EFE3E1',
              padding: '12px 28px',
              textDecoration: 'none',
              display: 'inline-block'
            }}>
            Arrange a Consultation
          </a>
        </div>

        {/* Mobile Burger */}
        <button
          className="lg:hidden flex flex-col justify-center gap-[5px] p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Open Menu"
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}>

          <span
            className="block transition-all duration-300"
            style={{
              width: '22px',
              height: '1px',
              backgroundColor: '#1A2332',
              transform: mobileMenuOpen ? 'rotate(45deg) translate(4px, 4px)' : 'none'
            }} />

          <span
            className="block transition-all duration-300"
            style={{
              width: '22px',
              height: '1px',
              backgroundColor: '#1A2332',
              opacity: mobileMenuOpen ? 0 : 1
            }} />

          <span
            className="block transition-all duration-300"
            style={{
              width: '22px',
              height: '1px',
              backgroundColor: '#1A2332',
              transform: mobileMenuOpen ? 'rotate(-45deg) translate(4px, -4px)' : 'none'
            }} />

        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className="lg:hidden overflow-hidden transition-all duration-400"
        style={{
          backgroundColor: '#EFE3E1',
          maxHeight: mounted && mobileMenuOpen ? '400px' : '0',
          borderTop: mounted && mobileMenuOpen ? '1px solid rgba(26,26,26,0.08)' : 'none'
        }}
        suppressHydrationWarning>

        <nav className="flex flex-col px-8 py-6 gap-5">
          {[
          { label: 'Home', href: '/' },
          { label: 'About', href: '/about' },
          { label: 'Psychological Services', href: '/psychological-services' },
          { label: 'Concierge Care', href: '/services' },
          { label: 'Appointments & Fees', href: '/enquiries' }].
          map(({ label, href }) =>
          <a
            key={href}
            href={href}
            style={{
              fontFamily: 'adobe-garamond-pro, Georgia, serif',
              fontSize: '18px',
              color: isActive(href) ? '#1A2332' : '#4A4A4A',
              textDecoration: isActive(href) ? 'underline' : 'none',
              textUnderlineOffset: '4px'
            }}>
              {label}
            </a>
          )}
          <a
            href="/contact"
            style={{
              fontFamily: 'adobe-garamond-pro, Georgia, serif',
              fontSize: '13px',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: '#EFE3E1',
              backgroundColor: '#1A2332',
              padding: '14px 24px',
              textDecoration: 'none',
              textAlign: 'center',
              marginTop: '8px',
              display: 'block'
            }}>
            Arrange a Consultation
          </a>
        </nav>
      </div>
    </header>);

}